import { farShared } from "./farTokens.jsx";

/**
 * Send page layout (two-column workspace + FAR status column).
 * Merged into homePageStyle in pages/Home.js. Classes are prefixed "sp".
 */
const sendPageStyle = (theme) => {
    const s = farShared(theme);
    const t = s.t;
    return {
        spPage: {
            position: "relative",
            zIndex: "2",
            maxWidth: "1240px",
            margin: "0 auto",
            padding: "88px 24px 48px",
            boxSizing: "border-box",
            textAlign: "left",
            color: t.text,
            [theme.breakpoints.down("sm")]: { padding: "76px 12px 24px" }
        },
        spHeader: {
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "16px 24px",
            flexWrap: "wrap",
            marginBottom: "20px"
        },
        spHeaderText: { flex: "1 1 320px", minWidth: 0 },
        spTitle: {
            margin: 0,
            fontSize: "30px",
            lineHeight: "38px",
            fontWeight: 700,
            color: theme.customize.pageTitleColor,
            [theme.breakpoints.down("sm")]: { fontSize: "24px", lineHeight: "30px" }
        },
        spSubtitle: { margin: "4px 0 10px", fontSize: "15px", color: t.textMuted },
        spBanner: { flex: "0 1 520px", minWidth: 0 },
        spLayout: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 400px)",
            gap: "20px",
            alignItems: "start",
            [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" }
        },
        spMain: {
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            // homepageFormFields adds float/overflow to direct div children; neutralize for the new layout.
            "& > div": { marginBottom: "0px !important", overflow: "visible !important" }
        },
        spSide: {
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            position: "sticky",
            top: "80px",
            [theme.breakpoints.down("md")]: { position: "static" }
        },
        spCard: {
            ...s.card,
            padding: "18px 18px 16px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            "& .formElement": { marginBottom: "0px" }
        },
        spCardTitle: {
            margin: 0,
            fontSize: "16px",
            fontWeight: 700,
            color: t.textStrong,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            "& svg": { width: "20px", height: "20px", color: t.dark ? t.link : t.accent }
        },
        spCardHint: { margin: "-6px 0 0", fontSize: "13px", color: t.textMuted, lineHeight: "18px" },
        spLabel: {
            position: "static !important",
            display: "block",
            margin: "0 0 6px !important",
            fontSize: "13px !important",
            fontWeight: 600,
            lineHeight: "18px",
            color: t.text + " !important",
            cursor: "default !important",
            maxWidth: "none !important",
            textAlign: "left !important"
        },
        spLabelMuted: { fontWeight: 400, color: t.textMuted },
        // The contacts button stretches to the input's height so both edges line up in every theme.
        spRecipientInputRow: {
            display: "flex",
            alignItems: "stretch",
            gap: "8px",
            "& > .MuiFormControl-root, & > div:first-child": { flex: "1 1 auto", minWidth: 0, margin: "0 !important" }
        },
        spContactsButton: {
            flexShrink: 0,
            alignSelf: "stretch",
            width: "44px",
            minHeight: 0,
            margin: 0,
            padding: 0,
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            border: "1px solid "+t.borderStrong,
            backgroundColor: t.inputBg,
            color: t.textStrong,
            cursor: "pointer",
            "&:hover": { borderColor: t.primary, color: t.primary },
            "&:focus-visible": { outline: "2px solid "+t.focus, outlineOffset: "2px" },
            "& svg": { fontSize: "22px" }
        },
        spHelperRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            flexWrap: "wrap",
            marginTop: "6px",
            "& p": { margin: 0, fontSize: "13px !important", color: t.textMuted + " !important" }
        },
        spFrom: {
            "& input": { fontSize: "14px" }
        },
        spTwoCol: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "12px",
            [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" }
        },
        spPresets: { display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" },
        spPreset: {
            ...s.focusRing,
            padding: "4px 10px",
            borderRadius: "999px",
            border: "1px solid "+t.borderStrong,
            background: "transparent",
            color: t.text,
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { backgroundColor: t.surfaceHover },
            "&[aria-pressed='true']": { backgroundColor: t.info.bg, borderColor: t.info.border, color: t.dark ? t.textStrong : t.info.fg }
        },
        spCheckRow: {
            "& .MuiFormControlLabel-root": { alignItems: "center !important" },
            "& .MuiCheckbox-root": { padding: "4px 10px 4px 0 !important" },
            "& .MuiFormControlLabel-label": { color: t.text + " !important", fontSize: "14px", fontWeight: 600 }
        },
        spCheckHelp: { margin: "2px 0 0 30px !important", fontSize: "13px !important", color: t.textMuted + " !important", lineHeight: "18px" },
        spDropZone: {
            borderRadius: "10px",
            border: "2px dashed "+t.borderStrong,
            padding: "14px",
            textAlign: "center",
            transition: "border-color 0.15s, background-color 0.15s",
            "& .add-files-button ul": {
                justifyContent: "center !important",
                gap: "18px",
                backgroundColor: "transparent !important",
                border: "none !important",
                padding: "4px !important"
            },
            "& .add-files-button .or-text": { color: t.textMuted }
        },
        spDropActive: { borderColor: t.accent, backgroundColor: t.info.bg },
        // Files source: Upload Files / Select a Folder / My Docs
        spSources: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: "10px"
        },
        spSource: {
            ...s.focusRing,
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "12px",
            minHeight: "64px",
            borderRadius: "10px",
            border: "1px solid "+t.borderStrong,
            backgroundColor: "transparent",
            color: t.text,
            textAlign: "left",
            font: "inherit",
            cursor: "pointer",
            "&:hover": { backgroundColor: t.surfaceHover },
            "& svg": { width: "24px", height: "24px", flexShrink: 0, color: t.dark ? t.link : t.accent },
            "& strong": { display: "block", fontSize: "14px", color: t.textStrong, lineHeight: "20px" },
            "& small": { display: "block", fontSize: "12px", color: t.textMuted, lineHeight: "16px", marginTop: "2px" },
            "&[aria-pressed='true']": {
                borderColor: t.accent,
                backgroundColor: t.info.bg,
                boxShadow: "inset 0 0 0 1px "+t.accent
            }
        },
        spUploaderHidden: { display: "none" },
        spBrowseButton: { ...s.secondaryButton, "& svg": { width: "20px", height: "20px" } },
        spLinkButton: {
            ...s.focusRing,
            border: "none",
            background: "transparent",
            padding: 0,
            font: "inherit",
            fontWeight: 600,
            color: t.dark ? t.link : t.accent,
            textDecoration: "underline",
            cursor: "pointer"
        },
        spSourceNote: { margin: 0, fontSize: "13px", color: t.textMuted },
        spDropHint: { margin: "6px 0 0", fontSize: "13px", color: t.textMuted },
        spFilesHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", fontSize: "13px", color: t.textMuted },
        spFileList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px" },
        spFile: {
            border: "1px solid "+t.border,
            borderRadius: "10px",
            padding: "10px 12px",
            backgroundColor: t.dark ? "rgba(255,255,255,0.02)" : t.surfaceAlt
        },
        spFileRow: { display: "flex", alignItems: "center", gap: "10px" },
        spFileIcon: { width: "28px", height: "28px", flexShrink: 0, objectFit: "contain" },
        spFileText: { flex: 1, minWidth: 0 },
        spFileName: { margin: 0, fontWeight: 600, fontSize: "14px", color: t.textStrong, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        spFileMeta: { margin: 0, fontSize: "12px", color: t.textMuted },
        spIconButton: s.ghostIconButton,
        spZipRow: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            "& .labelElement .MuiFormControlLabel-label": { color: t.text + " !important" },
            "& .lockElement svg": { fill: t.dark ? t.link : t.accent, color: t.dark ? t.link : t.accent }
        },
        spZipPassword: {
            "& .MuiInputLabel-root": { position: "static", transform: "none", marginBottom: "6px" }
        },
        spMessage: { padding: "10px 12px", borderRadius: "8px", fontSize: "14px", backgroundColor: t.danger.bg, border: "1px solid "+t.danger.border, color: t.danger.fg, "& p": { margin: 0 } },
        spSendBar: {
            ...s.card,
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            [theme.breakpoints.down("sm")]: {
                position: "sticky",
                bottom: "0",
                zIndex: 5,
                borderRadius: "12px 12px 0 0",
                boxShadow: t.shadow
            }
        },
        spSendStatus: { flex: "1 1 220px", minWidth: 0, fontSize: "14px", color: t.textMuted, margin: 0 },
        spSendButtons: { display: "flex", gap: "10px", flexWrap: "wrap", marginLeft: "auto" },
        spSend: {
            ...s.primaryButton,
            minWidth: "150px",
            "& svg": { width: "20px", height: "20px", fill: "currentColor" }
        },
        spException: s.secondaryButton,
        spSummaryList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "8px", fontSize: "14px" },
        spSummaryItem: { display: "flex", justifyContent: "space-between", gap: "10px", "& span:first-child": { color: t.textMuted } },
        spChipIcon: {
            width: "16px !important",
            height: "16px !important",
            verticalAlign: "-3px",
            marginRight: "4px"
        },
        spChipOk: { color: t.dark ? t.success.fg : t.success.solid },
        spChipBad: { color: t.dark ? t.danger.fg : t.danger.solid },
        spChipWarn: { color: t.dark ? t.warning.fg : t.warning.solid },
        spChipUnauthorized: {
            outline: "2px solid "+t.danger.solid,
            outlineOffset: "-2px"
        },
        spSrOnly: { position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", border: 0 }
    };
};

export default sendPageStyle;
